import { X } from 'lucide-react'
import * as React from 'react'
import type { FamilyRelation } from '../types/member'
import { FormLabel } from '@/components/custom/form-label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

type Option = { value: string; label: string }

const RELATIONS: Array<FamilyRelation> = [
  'FATHER',
  'MOTHER',
  'SON',
  'DAUGHTER',
  'SPOUSE',
  'BROTHER',
  'SISTER',
  'GUARDIAN',
  'OTHER',
]

export function FamilyMembersSelector({
  field,
  label,
  options,
  id,
  name,
  required,
  className,
}: {
  field: any
  label?: string
  options: Array<Option>
  id?: string
  name?: string
  required?: boolean
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const selected = (
    Array.isArray(field.state.value) ? field.state.value : []
  ) as Array<{
    memberId: string
    relation: FamilyRelation
  }>

  const byId = React.useMemo(
    () => new Map(options.map((o) => [o.value, o.label])),
    [options],
  )

  const filtered = React.useMemo(() => {
    const lower = query.trim().toLowerCase()
    const selectedIds = new Set(selected.map((s) => s.memberId))
    return options.filter(
      (o) =>
        !selectedIds.has(o.value) &&
        (!lower || o.label.toLowerCase().includes(lower)),
    )
  }, [options, query, selected])

  const addMember = (memberId: string) => {
    const next = [
      ...selected,
      { memberId, relation: 'OTHER' as FamilyRelation },
    ]
    field.handleChange(next)
  }

  const removeMember = (memberId: string) => {
    field.handleChange(selected.filter((m) => m.memberId !== memberId))
  }

  const changeRelation = (memberId: string, relation: FamilyRelation) => {
    field.handleChange(
      selected.map((m) => (m.memberId === memberId ? { ...m, relation } : m)),
    )
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <FormLabel
          htmlFor={id || name}
          className="mb-1 block text-sm"
          required={required}
        >
          {label}
        </FormLabel>
      )}
      <div className="rounded-md border border-input p-2">
        <div className="flex flex-col gap-2">
          {/* Selected list */}
          {selected.length > 0 && (
            <ul className="flex flex-col gap-2">
              {selected.map((sel) => (
                <li key={sel.memberId} className="flex items-center gap-2">
                  <span
                    className="text-sm truncate flex-1"
                    title={byId.get(sel.memberId) || sel.memberId}
                  >
                    {byId.get(sel.memberId) || sel.memberId}
                  </span>
                  <Select
                    value={sel.relation}
                    onValueChange={(v) =>
                      changeRelation(sel.memberId, v as FamilyRelation)
                    }
                  >
                    <SelectTrigger size="sm">
                      <SelectValue placeholder="Relation" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r.charAt(0) + r.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    aria-label="Remove family member"
                    className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground"
                    onClick={() => removeMember(sel.memberId)}
                  >
                    <X className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Add more */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="border-input text-muted-foreground hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex h-8 items-center justify-center gap-2 rounded-md border bg-transparent px-3 text-sm outline-none focus-visible:ring-[3px] hover:cursor-pointer"
              >
                Add family member
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Search members..."
                />
                <CommandList>
                  <CommandEmpty>No members found.</CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-auto">
                    {filtered.map((o) => (
                      <CommandItem
                        key={o.value}
                        onSelect={() => {
                          addMember(o.value)
                          setQuery('')
                          setOpen(false)
                        }}
                        className="cursor-pointer"
                      >
                        {o.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {field.state.meta.isTouched && !field.state.meta.isValid ? (
        <p className="text-xs text-red-500">
          {field.state.meta.errors.join(', ')}
        </p>
      ) : null}
      <p className="text-xs">
        {field.state.meta.isValidating ? 'Validating...' : null}
      </p>
    </div>
  )
}
